// Registre des sessions du navigateur, par conversation, hors React : le panneau, la carte
// « Navigateur » et l'ouverture automatique lisent la meme session. Le jeton ne passe JAMAIS par
// l'adresse de la page (le hash du panneau vaut juste « navigateur »).
import type { GesteNavigateur } from "@app/lib/compta/navigateur/libelles";
import type {
  SessionDatee,
  SessionNavigateur,
} from "@app/lib/compta/navigateur/session";

export interface GesteDate {
  geste: GesteNavigateur;
  url: string | null;
  a: number;
  actionId: string;
}

export interface RegistreNavigateur {
  enregistrer: (conversationId: string, entree: SessionDatee) => void;
  session: (conversationId: string) => SessionNavigateur | null;
  /** Vrai la premiere fois pour une conversation, faux ensuite : une seule ouverture auto. */
  reserverOuvertureAuto: (conversationId: string) => boolean;
  dejaOuvertAuto: (conversationId: string) => boolean;
  /** Dernier geste de l'assistant (outil du navigateur), pour la narration du panneau. */
  noterGeste: (conversationId: string, geste: GesteDate) => void;
  geste: (conversationId: string) => GesteDate | null;
  abonner: (rappel: () => void) => () => void;
}

export function creerRegistreNavigateur(): RegistreNavigateur {
  const sessions = new Map<string, SessionDatee>();
  const ouvertes = new Set<string>();
  const gestes = new Map<string, GesteDate>();
  const abonnes = new Set<() => void>();
  const prevenir = () => {
    for (const rappel of abonnes) {
      rappel();
    }
  };

  return {
    enregistrer(conversationId, entree) {
      const actuelle = sessions.get(conversationId);
      if (actuelle && actuelle.horodatage > entree.horodatage) {
        return;
      }
      if (
        actuelle &&
        actuelle.actionId === entree.actionId &&
        actuelle.session.streamUrl === entree.session.streamUrl
      ) {
        return;
      }
      sessions.set(conversationId, entree);
      prevenir();
    },
    session(conversationId) {
      return sessions.get(conversationId)?.session ?? null;
    },
    reserverOuvertureAuto(conversationId) {
      if (ouvertes.has(conversationId)) {
        return false;
      }
      ouvertes.add(conversationId);
      return true;
    },
    dejaOuvertAuto(conversationId) {
      return ouvertes.has(conversationId);
    },
    noterGeste(conversationId, geste) {
      const actuel = gestes.get(conversationId);
      if (
        actuel &&
        (actuel.actionId === geste.actionId || actuel.a > geste.a)
      ) {
        return;
      }
      gestes.set(conversationId, geste);
      prevenir();
    },
    geste(conversationId) {
      return gestes.get(conversationId) ?? null;
    },
    abonner(rappel) {
      abonnes.add(rappel);
      return () => {
        abonnes.delete(rappel);
      };
    },
  };
}

/** Le registre de l'onglet : une instance pour toute la SPA. */
export const registreNavigateur = creerRegistreNavigateur();

export interface ContexteOuverture {
  /** « flux » : resultat arrive pendant que l'agent repond ; « chargement » : deja la. */
  origine: "flux" | "chargement";
  dejaOuvertePourCetteConversation: boolean;
  panneauAffiche: boolean;
  /** Mobile ou extension : le panneau couvrirait la conversation, on n'ouvre rien seul. */
  affichageSuperpose: boolean;
  /** Au chargement, resultat de GET etatUrl (faux sur 401) ; inconnu tant qu'il n'a pas repondu. */
  sessionVivante?: boolean;
}

export function doitOuvrirAutomatiquement(c: ContexteOuverture): boolean {
  if (c.dejaOuvertePourCetteConversation || c.panneauAffiche) {
    return false;
  }
  if (c.affichageSuperpose) {
    return false;
  }
  if (c.origine === "chargement") {
    return c.sessionVivante === true;
  }
  return true;
}
